import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { IconChevronDown, IconTrash, IconArrowRight } from '../components/Icons';
import { isCloudVisionEnabled, setCloudVisionEnabled } from '../lib/vision';
import { isReadAloudEnabled, setReadAloudEnabled } from '../lib/speech';
import { getUserName, setUserName } from '../lib/profile';
import { telemetry } from '../lib/telemetry';

// Settings, lifted from MamaMind's accordion-section pattern. Sections collapse,
// the Danger Zone is bordered in red, and every action that destroys state
// requires explicit confirmation.

interface AccordionProps {
  title: string;
  danger?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

function Accordion({ title, danger, defaultOpen = false, children }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className={`rounded-2xl overflow-hidden border ${
        danger ? 'border-red-300 bg-red-50/40' : 'border-ink/5 bg-white/70'
      }`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition ${
          danger ? 'text-red-600' : 'text-accent'
        }`}
      >
        <span className="text-xs uppercase tracking-wider font-medium">{title}</span>
        <span className={`transition ${open ? 'rotate-180' : ''}`}>
          <IconChevronDown size={16} />
        </span>
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </section>
  );
}

function Toggle({ on, onChange, label, sub }: { on: boolean; onChange: (v: boolean) => void; label: string; sub: string }) {
  return (
    <label className="flex items-start justify-between gap-3 py-2">
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        <span className="block text-xs text-ink/50 leading-relaxed">{sub}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(!on)}
        className={`w-11 h-6 rounded-full transition relative flex-shrink-0 ${on ? 'bg-accent' : 'bg-ink/15'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition ${
            on ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </label>
  );
}

export function SettingsScreen() {
  const navigate = useNavigate();
  const [cloud, setCloud] = useState(isCloudVisionEnabled());
  const [readAloud, setReadAloud] = useState(isReadAloudEnabled());
  const [tele, setTele] = useState(!telemetry.isOptedOut());
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState(getUserName());
  const [nameSaved, setNameSaved] = useState(false);

  function saveName() {
    setUserName(nameInput.trim());
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
  }

  function flipCloud(next: boolean) {
    setCloudVisionEnabled(next);
    setCloud(next);
  }
  function flipReadAloud(next: boolean) {
    setReadAloudEnabled(next);
    setReadAloud(next);
  }
  function flipTelemetry(next: boolean) {
    telemetry.setOptOut(!next);
    setTele(next);
  }

  function resetDevice() {
    try {
      const keysToWipe: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('mdt_')) keysToWipe.push(k);
      }
      keysToWipe.forEach((k) => localStorage.removeItem(k));
      setResetMsg('Device reset. Cloud AI and telemetry preferences cleared.');
      setCloud(false);
      setTele(true);
      setConfirmReset(false);
    } catch {
      setResetMsg('Could not reset device. Try clearing your browser site data.');
    }
  }

  return (
    <AppShell title="Settings">
      <div className="px-5 pt-2 pb-6">
        <section className="py-4">
          <h1 className="text-2xl font-medium text-ink">Settings</h1>
          <p className="mt-2 text-sm text-ink/60 leading-relaxed">
            All settings live on this device. There is no account to log into.
          </p>
        </section>

        <div className="space-y-3">
          <Accordion title="Profile" defaultOpen>
            <div className="space-y-2">
              <label htmlFor="profile-name" className="block text-sm font-medium text-ink">
                Your name
              </label>
              <p className="text-xs text-ink/55 leading-relaxed">
                Used on the worksheets you save. The auto-title looks like
                "{nameInput || 'Your name'} - Maths - Year 4 - 12 May 2026". Stored on this
                device only.
              </p>
              <div className="flex gap-2">
                <input
                  id="profile-name"
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="First name"
                  className="flex-1 rounded-xl border border-ink/10 bg-cream px-3 py-2 text-ink focus:outline-none focus:ring-4 focus:ring-accent/30"
                  maxLength={40}
                  autoComplete="given-name"
                />
                <button
                  onClick={saveName}
                  className="px-4 py-2 rounded-xl bg-accent text-white text-sm font-medium hover:brightness-105 transition"
                >
                  Save
                </button>
              </div>
              {nameSaved && <p className="text-xs text-green-700">Saved.</p>}
            </div>
          </Accordion>

          <Accordion title="AI mode">
            <Toggle
              on={cloud}
              onChange={flipCloud}
              label="Use AI to read worksheets"
              sub="On by default. Sends the photo to our server for processing, then deletes it immediately. The same safeguarding rules apply (no body images, no faces, document-only)."
            />
            <p className="text-xs text-ink/50 leading-relaxed pt-2 border-t border-ink/5">
              When AI mode is off, the app still works. You tap the slot you want to answer, then
              dictate or type. Nothing leaves your device. Slot detection is less accurate.
            </p>
          </Accordion>

          <Accordion title="Reading aloud">
            <Toggle
              on={readAloud}
              onChange={flipReadAloud}
              label="Read each question aloud"
              sub="On by default. Plays the question through a calm voice when you reach it. Pause, stop, or replay any time from the question card."
            />
            <p className="text-xs text-ink/50 leading-relaxed pt-2 border-t border-ink/5">
              When this is off, the app still shows the question on screen but does not read it
              aloud. Useful if your child prefers to read it themselves, or if you are in a quiet
              space.
            </p>
          </Accordion>

          <Accordion title="Privacy and data">
            <Toggle
              on={tele}
              onChange={flipTelemetry}
              label="Help improve the app"
              sub="Sends anonymous events such as 'a worksheet was finished'. Never the photo, never the answers, never anything that could identify you."
            />
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between py-3 border-t border-ink/5 text-left"
            >
              <span>
                <span className="block text-sm font-medium text-ink">Privacy policy</span>
                <span className="block text-xs text-ink/50">Plain English. Updated 12 May 2026.</span>
              </span>
              <IconArrowRight size={16} />
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between py-3 border-t border-ink/5 text-left"
            >
              <span>
                <span className="block text-sm font-medium text-ink">Terms of service</span>
                <span className="block text-xs text-ink/50">What you agree to when using the app.</span>
              </span>
              <IconArrowRight size={16} />
            </button>
          </Accordion>

          <Accordion title="Support">
            <button
              onClick={() => navigate('/support')}
              className="w-full flex items-center justify-between py-3 text-left"
            >
              <span>
                <span className="block text-sm font-medium text-ink">Get help</span>
                <span className="block text-xs text-ink/50">FAQs, common issues, contact email.</span>
              </span>
              <IconArrowRight size={16} />
            </button>
            <a
              href="mailto:support@mydyslexiatools.com?subject=Worksheet%20Filler%20feedback"
              className="w-full flex items-center justify-between py-3 border-t border-ink/5 text-left"
            >
              <span>
                <span className="block text-sm font-medium text-ink">Send feedback</span>
                <span className="block text-xs text-ink/50">support@mydyslexiatools.com</span>
              </span>
              <IconArrowRight size={16} />
            </a>
          </Accordion>

          <Accordion title="About">
            <div className="text-xs text-ink/60 space-y-2">
              <p>
                Answer Assist is part of <strong className="font-medium text-ink">My Dyslexia Tools</strong>, a portfolio of small apps built by Creative Sauce Ltd.
              </p>
              <p>Version 0.1.0. Source on GitHub at sarahwood-dev/mydyslexiatools.</p>
              <p>(c) {new Date().getFullYear()} Creative Sauce Ltd.</p>
            </div>
          </Accordion>

          <Accordion title="Danger zone" danger>
            {!confirmReset ? (
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-red-600">Reset this device</p>
                  <p className="text-xs text-red-600/80 mt-1 leading-relaxed">
                    Clears the anonymous identifier, your AI mode preference, and any local
                    settings. Any in-progress worksheet is discarded. This cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => setConfirmReset(true)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-red-600 border border-red-300 hover:bg-red-50 transition"
                >
                  <IconTrash size={14} /> Reset
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-red-600 leading-relaxed">
                  This will clear all device-local settings. Confirm to proceed.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={resetDevice}
                    className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition"
                  >
                    Yes, reset the device
                  </button>
                  <button
                    onClick={() => setConfirmReset(false)}
                    className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium text-ink/70 bg-white border border-ink/10 hover:bg-ink/5 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {resetMsg && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {resetMsg}
              </p>
            )}
          </Accordion>
        </div>

        <p className="mt-8 pt-4 border-t border-ink/5 text-[11px] text-ink/40 text-center leading-relaxed">
          My Dyslexia Tools is a Creative Sauce Ltd product. Registered in England and Wales.
        </p>
      </div>
    </AppShell>
  );
}
